// Package Imports
import { useState } from 'react'
import { notify } from '@/core/lib/ui-bus'
import { useNavigate } from '@tanstack/react-router'
import { AnimatePresence, motion } from 'motion/react'
import { IoChevronDown, IoMenu } from 'react-icons/io5'
import { RiMoonClearLine, RiSparkling2Line } from 'react-icons/ri'

// Core Imports
import { useAuth } from '@/core/hooks/use-auth'
import { signOut } from '@/core/utils/auth-utils'
import { useOutsideClick } from '@/core/hooks/use-outside-click'
import { displayBackendError } from '@/core/utils/display-backend-error'
import { useTheme } from '@/core/hooks/use-theme'

// Module Imports
import { useSignOut } from '@/modules/auth/auth.hooks'

type THeaderProps = {
  setSidebarOpen: (sidebarOpen: boolean) => void
}

const Header = ({ setSidebarOpen }: THeaderProps) => {
  const navigate = useNavigate()
  const ref = useOutsideClick(() => setProfileOpen(false))

  const { user } = useAuth()
  const { theme, toggleTheme } = useTheme()
  const isCanli = theme === 'canli'

  const signOutMutation = useSignOut({
    onSuccess: async () => {
      await navigate({
        to: '/',
        search: {
          status: 'signedOut',
        },
      })
      await signOut()
    },
    onError: () => {
      notify.error({
        message: 'Hata',
        description: displayBackendError('auth', 'GENERAL_ERROR'),
        placement: 'topRight',
      })
    },
  })

  const [profileOpen, setProfileOpen] = useState<boolean>(false)

  const handleSignOut = async () => {
    setProfileOpen(false)
    await signOutMutation.mutateAsync(null)
  }

  const headerClass = isCanli
    ? 'fixed top-0 right-0 left-0 z-1 flex h-16 items-center justify-between border-b border-[var(--app-border)] bg-[var(--app-surface)]/80 px-4 backdrop-blur-md lg:px-6'
    : 'fixed top-0 right-0 left-0 z-1 flex h-16 items-center justify-between bg-white px-4 shadow-lg lg:px-6'

  const menuBtnClass = isCanli
    ? 'rounded-md p-1 text-[var(--app-text)] hover:bg-[var(--app-surface-2)] lg:hidden'
    : 'rounded-md p-1 hover:bg-gray-100 lg:hidden'

  const nameClass = isCanli
    ? 'ml-2 text-sm font-medium capitalize text-[var(--app-text)]'
    : 'ml-2 text-sm font-medium text-gray-700 capitalize'

  const dropdownClass = isCanli
    ? 'absolute right-0 mt-2 w-48 origin-top-right rounded-xl border border-[var(--app-border)] bg-[var(--app-surface)] py-1 shadow-2xl focus:outline-none'
    : 'absolute right-0 mt-2 w-48 origin-top-right rounded-md bg-white py-1 shadow-lg focus:outline-none'

  const signOutBtnClass = isCanli
    ? 'block w-full cursor-pointer px-4 py-2 text-left text-sm text-[var(--app-text)] hover:bg-[var(--app-surface-2)]'
    : 'block w-full cursor-pointer px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100'

  return (
    <header className={headerClass}>
      <div className="flex items-center">
        <button
          onClick={() => setSidebarOpen(true)}
          data-testid="open-sidebar"
          className={menuBtnClass}
        >
          <IoMenu className="h-6 w-6" />
        </button>
      </div>
      <div className="flex items-center gap-4">
        <motion.button
          type="button"
          onClick={(event) =>
            toggleTheme({ clientX: event.clientX, clientY: event.clientY })
          }
          data-testid="theme-toggle"
          aria-label="Tema değiştir"
          whileTap={{ scale: 0.9, rotate: -8 }}
          whileHover={{ scale: 1.06 }}
          className={
            isCanli
              ? 'relative inline-flex h-9 cursor-pointer items-center gap-2 overflow-hidden rounded-full border border-[var(--app-border)] bg-gradient-to-r from-[#7c5cff] via-[#22d3ee] to-[#ec4899] px-3 text-xs font-semibold text-white shadow-[0_8px_24px_-8px_rgba(124,92,255,0.7)]'
              : 'relative inline-flex h-9 cursor-pointer items-center gap-2 rounded-full border border-gray-200 bg-white px-3 text-xs font-semibold text-gray-700 hover:bg-gray-50'
          }
        >
          {/*
           * Plain inline content (no AnimatePresence). The theme swap is
           * already animated by the document-level startViewTransition in
           * theme.store.ts. Stacking motion.span exit/enter on top of the
           * view-transition snapshot caused the label to occasionally render
           * as an empty box: AnimatePresence's exit ran on the snapshot
           * while the new key never mounted live before the snapshot was
           * torn down. Single source of animation = stable text.
           */}
          <span className="inline-flex items-center gap-2">
            {isCanli ? (
              <>
                <RiSparkling2Line className="h-4 w-4" />
                Canlı
              </>
            ) : (
              <>
                <RiMoonClearLine className="h-4 w-4" />
                Klasik
              </>
            )}
          </span>
        </motion.button>
        <div ref={ref} className="flex cursor-pointer items-center">
          <div className="relative">
            <button
              data-testid="profile-button"
              className="flex cursor-pointer items-center rounded-full text-sm outline-none"
              onClick={() => setProfileOpen(!profileOpen)}
            >
              <span data-testid="profile-name" className={nameClass}>
                {user?.name}
              </span>
              <IoChevronDown
                className={isCanli ? 'ml-1 text-[var(--app-text-dim)]' : 'ml-1'}
              />
            </button>
            <AnimatePresence>
              {profileOpen && (
                <motion.div
                  key="dropdown"
                  data-testid="profile-dropdown"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 4 }}
                  exit={{ opacity: 0, y: 20 }}
                  transition={{ duration: 0.3 }}
                  className={dropdownClass}
                >
                  <button
                    data-testid="signout-button"
                    onClick={handleSignOut}
                    className={signOutBtnClass}
                  >
                    Çıkış Yap
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </header>
  )
}

export default Header
